import { useState, useRef, useEffect, useCallback } from "react";
import { LogOut, Users, Sun, Moon, Monitor, GraduationCap, Briefcase, Menu, X, CalendarDays, Target, Lock, ListChecks, MessageSquare } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useNavigate, useLocation } from "react-router-dom";
import { t } from "@/i18n";
import { useNotifikationer } from "@/contexts/NotifikationerContext";
import ChatPanel from "@/components/ChatPanel";

function ChatPillTag({ antal, aktiv, onClick }) {
  const containerRef = useRef(null);
  const prevAntal = useRef(antal);

  useEffect(() => {
    if (antal > 0 && antal !== prevAntal.current) {
      const el = containerRef.current;
      if (!el) return;
      el.classList.remove("chat-pill-blink");
      void el.offsetWidth;
      el.classList.add("chat-pill-blink");
      const tid = setTimeout(() => el.classList.remove("chat-pill-blink"), 1100);
      prevAntal.current = antal;
      return () => clearTimeout(tid);
    }
    prevAntal.current = antal;
  }, [antal]);

  const harUlæst = antal > 0;
  const erAktiv = aktiv || harUlæst;

  return (
    <div ref={containerRef} className="flex items-center rounded-full border border-border bg-muted p-0.5 gap-0.5">
      <button
        onClick={onClick}
        title="Beskeder & Notifikationer"
        className={`flex items-center gap-1.5 px-1.5 py-1.5 rounded-full transition-colors duration-150 ${
          erAktiv ? "bg-[#FF2A6D] text-white shadow-sm" : "text-muted-foreground hover:text-foreground"
        }`}
      >
        <MessageSquare className="w-3 h-3 shrink-0" />
        <span className="text-[12px] font-semibold leading-none pr-0.5">
          {antal > 99 ? "99+" : antal || "0"}
        </span>
      </button>
    </div>
  );
}

const DISPLAY_NAMES = { "Koebenhavn": "København" };
const displayName = (name) => DISPLAY_NAMES[name] || name;

export const DashboardHeader = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  const lang = user?.lang || "da";
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const { ulæstBeskeder, ulæstNotifikationer, chatPanelOpen, setChatPanelOpen } = useNotifikationer();

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const isAdmin = ["admin", "superadmin", "owner", "api_admin"].includes(user?.role);
  const isSuperAdmin = ["superadmin", "owner", "api_admin"].includes(user?.role);
  // #38.6: Light-medarbejder (vikar/læge) — begrænset visning. Ser KUN Hub +
  // Undervisning i top-nav, men må gerne se medarbejder-kontaktlisten.
  const isLight = user?.role === "light";
  // Alle ansatte (member og opefter, inkl. light) — bruges til Medarbejdere-
  // oversigten (kontaktliste), som er åben for alle ansatte. Stamdata-profilen
  // styres separat inde på siden.
  const erAnsat = ["member", "light", "admin", "superadmin", "owner", "api_admin"].includes(user?.role);

  // Individuelle rettigheder for Medarbejdere (admin/superadmin har alle on automatisk)
  const rettigheder = user?.rettigheder || {};
  const harRet = (navn) => isAdmin || !!rettigheder[navn];
  // #48.1: alle almindelige medarbejdere (role 'member') ser Vagtplan-fanen —
  // uden vagtplan-rettighed får de en simplificeret, skrivebeskyttet visning
  // (gated inde i VagtplanPage). Light/guest ser den IKKE.
  const kanSeVagtplan     = harRet("kan_se_vagtplan") || user?.role === "member";
  const kanSeElever       = harRet("kan_se_elever");
  const kanSeMedarbejdere = harRet("kan_se_medarbejdere");

  const roleBadge = {
    owner:      { label: "SuperAdmin", color: "bg-violet-500/10 text-violet-700 dark:text-violet-400 border-violet-500/20" },
    superadmin: { label: "SuperAdmin", color: "bg-violet-500/10 text-violet-700 dark:text-violet-400 border-violet-500/20" },
    admin:      { label: t(lang, "roleAdmin"),  color: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20" },
    member:     { label: t(lang, "roleMember"), color: "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/20" },
    light:      { label: "Medarbejder (light)", color: "bg-teal-500/10 text-teal-700 dark:text-teal-300 border-teal-500/20" },
    guest:      { label: t(lang, "roleGuest"),  color: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20" },
  }[user?.role] || { label: t(lang, "roleUser"), color: "bg-secondary text-muted-foreground border-border" };

  // Luk menu ved klik udenfor
  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  const navItems = [
    // Brugere-oversigten (tidl. Medarbejdere): alle ansatte (member og opefter).
    ...(erAnsat ? [
      { label: "Brugere",       icon: <Briefcase className="w-4 h-4" />,  path: "/admin/medarbejdere",   active: location.pathname.startsWith("/admin/medarbejdere") },
    ] : []),
    // Vagtplan: alle med kan_se_vagtplan (inkl. admin/superadmin via harRet)
    ...(kanSeVagtplan ? [
      { label: "Vagtplan",      icon: <CalendarDays className="w-4 h-4" />, path: "/admin/vagtplan",      active: location.pathname === "/admin/vagtplan" },
    ] : []),
    // Rekruttering (tidl. "Elever"): alle med kan_se_elever (inkl. admin/superadmin)
    ...(kanSeElever ? [
      { label: "Rekruttering",  icon: <GraduationCap className="w-4 h-4" />, path: "/admin/elever",      active: location.pathname === "/admin/elever" || location.pathname === "/admin/elev-status" },
    ] : []),
    // BrugerLog: KUN admin/superadmin — vises med låse-ikon
    ...(isAdmin ? [
      { label: "BrugerLog",     icon: <Users className="w-4 h-4" />,      path: "/admin/users",          active: location.pathname === "/admin/users", locked: true },
    ] : []),
    // Mål & Regler: KUN superadmin — vises med låse-ikon
    ...(isSuperAdmin ? [
      { label: "Mål & Regler",  icon: <Target className="w-4 h-4" />,     path: "/admin/maal-og-regler", active: location.pathname === "/admin/maal-og-regler", locked: true },
    ] : []),
    // Lister: KUN superadmin — samme adgangskrav som Mål & Regler
    ...(isSuperAdmin ? [
      { label: "Lister",        icon: <ListChecks className="w-4 h-4" />, path: "/admin/lister",         active: location.pathname === "/admin/lister", locked: true },
    ] : []),
  ];

  const handleNav = (item) => {
    navigate(item.path);
    setMenuOpen(false);
  };

  return (
  <>
    <header
      className="sticky top-0 z-50 backdrop-blur-xl bg-background/80 border-b border-border"
      data-testid="dashboard-header"
    >
      <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <a href="/" className="text-xl sm:text-2xl font-extrabold tracking-tighter font-[Manrope]" data-testid="app-title">
            <span className="text-[#FF2A6D]">Beauty & Style</span>
            <span className="text-foreground ml-1">Hub</span>
          </a>
        </div>

        <div className="flex items-center gap-3">
          {/* Desktop nav — skjult på mobil */}
          <div className="hidden md:flex items-center gap-2">
            {navItems.map(item => (
              <button key={item.path}
                onClick={() => handleNav(item)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold transition-colors duration-200 ${item.active ? "bg-secondary text-foreground" : "bg-transparent text-muted-foreground hover:text-foreground hover:bg-secondary"}`}>
                {item.icon}
                <span>{item.label}</span>
                {item.locked && <Lock className="w-3 h-3 ml-0.5 text-violet-500" />}
              </button>
            ))}
          </div>

          {/* Theme toggle — desktop kun */}
          <div className="hidden md:flex items-center rounded-full border border-border bg-muted p-0.5 gap-0.5">
            {[
              { val: "light", icon: <Sun className="w-3 h-3" />, label: "Lys" },
              { val: "system", icon: <Monitor className="w-3 h-3" />, label: "System" },
              { val: "dark", icon: <Moon className="w-3 h-3" />, label: "Mørk" },
            ].map(({ val, icon, label }) => (
              <button key={val} onClick={() => setTheme(val)} title={label}
                className={`p-1.5 rounded-full transition-colors duration-150 ${theme === val ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
                {icon}
              </button>
            ))}
          </div>

          {/* Beskeder-knap — desktop */}
          <div className="hidden md:flex items-center gap-1">
            <ChatPillTag
              antal={ulæstBeskeder + ulæstNotifikationer}
              aktiv={chatPanelOpen}
              onClick={() => setChatPanelOpen(o => !o)}
            />
          </div>

          {/* Avatar + logout */}
          <div className="flex items-center gap-2 md:pl-2 md:border-l md:border-border">
            <div className="hidden md:flex flex-col items-end">
              <span className="text-[13px] text-foreground font-medium leading-tight" data-testid="user-name">{user?.name}</span>
              <div className="flex items-center gap-1.5">
                <span className={`text-[12px] px-1.5 py-0.5 rounded-full border whitespace-nowrap ${roleBadge.color}`} data-testid="user-role">{roleBadge.label}</span>
                {user?.city && <span className="text-[12px] text-muted-foreground" data-testid="user-city">{displayName(user.city)}</span>}
              </div>
            </div>

            {(() => {
              const minItemId = user?.medarbejder_item_id;
              // #44.6: enhver medarbejder kan tilgå sit EGET stamkort via billedet
              // (uden Medarbejdere-rettighed). Faner gates inde på siden:
              // Stamdata + Arbejdsplan = se, CV = se+rediger, Fortrolige = skjult.
              const kanGaaTilEgenProfil = !!minItemId;
              const avatarInner = user?.image_file_id ? (
                <img src={`${API}/podio/image/${user.image_file_id}`} alt={user?.name}
                  className="w-9 h-9 rounded-full object-cover border border-border flex-shrink-0" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                  <span className="text-[13px] font-semibold text-muted-foreground">{user?.name?.charAt(0)?.toUpperCase() || "?"}</span>
                </div>
              );
              if (kanGaaTilEgenProfil) {
                return (
                  <button
                    onClick={() => navigate(`/admin/medarbejdere/${minItemId}`)}
                    className="hidden md:block rounded-full focus:outline-none focus:ring-2 focus:ring-[#FF2A6D]/40 hover:opacity-80 transition-opacity"
                    title="Gå til min profil"
                    data-testid="user-avatar-link"
                  >
                    {avatarInner}
                  </button>
                );
              }
              return <span className="hidden md:block">{avatarInner}</span>;
            })()}

            <button onClick={onLogout}
              className="hidden md:flex p-2 rounded-full hover:bg-red-500/10 transition-colors duration-200"
              data-testid="logout-button">
              <LogOut className="w-4 h-4 text-muted-foreground hover:text-red-400" />
            </button>
          </div>

          {/* Hamburger + beskeder — kun på mobil */}
          {navItems.length > 0 && (
            <div className="relative md:hidden flex items-center gap-1" ref={menuRef}>
              <ChatPillTag
                antal={ulæstBeskeder + ulæstNotifikationer}
                aktiv={chatPanelOpen}
                onClick={() => setChatPanelOpen(o => !o)}
              />
              <button onClick={() => setMenuOpen(o => !o)}
                className="p-2 rounded-full hover:bg-secondary transition-colors duration-200">
                {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>

              {menuOpen && (
                <div className="absolute right-0 top-full mt-2 w-56 bg-background border border-border rounded-2xl shadow-xl overflow-hidden z-50">
                  {/* Bruger-info — klikbar hvis man har en medarbejder-profil */}
                  {user?.medarbejder_item_id ? (
                    <button
                      onClick={() => { setMenuOpen(false); navigate(`/admin/medarbejdere/${user.medarbejder_item_id}`); }}
                      className="w-full px-4 py-3 border-b border-border bg-secondary/30 flex items-center gap-3 hover:bg-secondary/50 transition-colors text-left"
                    >
                      {user?.image_file_id ? (
                        <img src={`${API}/podio/image/${user.image_file_id}`} alt={user?.name}
                          className="w-8 h-8 rounded-full object-cover border border-border shrink-0" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center shrink-0">
                          <span className="text-[13px] font-semibold text-muted-foreground">{user?.name?.charAt(0)?.toUpperCase() || "?"}</span>
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-semibold text-foreground truncate">{user?.name}</p>
                        <span className={`text-[12px] px-1.5 py-0.5 rounded-full border whitespace-nowrap ${roleBadge.color}`}>{roleBadge.label}</span>
                      </div>
                      <svg className="w-4 h-4 text-muted-foreground shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
                    </button>
                  ) : (
                    <div className="px-4 py-3 border-b border-border bg-secondary/30 flex items-center gap-3">
                      {user?.image_file_id ? (
                        <img src={`${API}/podio/image/${user.image_file_id}`} alt={user?.name}
                          className="w-8 h-8 rounded-full object-cover border border-border shrink-0" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center shrink-0">
                          <span className="text-[13px] font-semibold text-muted-foreground">{user?.name?.charAt(0)?.toUpperCase() || "?"}</span>
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="text-[13px] font-semibold text-foreground truncate">{user?.name}</p>
                        <span className={`text-[12px] px-1.5 py-0.5 rounded-full border whitespace-nowrap ${roleBadge.color}`}>{roleBadge.label}</span>
                      </div>
                    </div>
                  )}

                  {/* Nav-punkter */}
                  <div className="py-1">
                    {navItems.map(item => (
                      <button key={item.path} onClick={() => handleNav(item)}
                        className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors ${item.active ? "text-foreground bg-secondary/50" : "text-muted-foreground hover:text-foreground hover:bg-secondary/30"}`}>
                        {item.icon}
                        <span className="flex-1 text-left">{item.label}</span>
                        {item.locked && <Lock className="w-3.5 h-3.5 text-violet-500" />}
                      </button>
                    ))}
                  </div>

                  {/* Theme toggle i menuen */}
                  <div className="px-4 py-3 border-t border-border">
                    <p className="text-[13px] text-muted-foreground mb-2 font-medium uppercase tracking-wide">Tema</p>
                    <div className="flex items-center rounded-full border border-border bg-muted p-0.5 gap-0.5">
                      {[
                        { val: "light", icon: <Sun className="w-3.5 h-3.5" />, label: "Lys" },
                        { val: "system", icon: <Monitor className="w-3.5 h-3.5" />, label: "System" },
                        { val: "dark", icon: <Moon className="w-3.5 h-3.5" />, label: "Mørk" },
                      ].map(({ val, icon, label }) => (
                        <button key={val} onClick={() => setTheme(val)} title={label}
                          className={`flex-1 flex items-center justify-center gap-1 py-1.5 rounded-full text-[13px] font-medium transition-colors duration-150 ${theme === val ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
                          {icon}
                          <span>{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Log ud */}
                  <div className="px-2 pb-2">
                    <button onClick={() => { setMenuOpen(false); onLogout(); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-500 hover:bg-red-500/10 transition-colors">
                      <LogOut className="w-4 h-4" />
                      Log ud
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Logout på mobil hvis ingen nav-punkter (guest) */}
          {navItems.length === 0 && (
            <button onClick={onLogout}
              className="md:hidden p-2 rounded-full hover:bg-red-500/10 transition-colors duration-200"
              data-testid="logout-button">
              <LogOut className="w-4 h-4 text-muted-foreground" />
            </button>
          )}
        </div>
      </div>
    </header>

    <ChatPanel open={chatPanelOpen} onClose={() => setChatPanelOpen(false)} />
  </>
  );
};
